/**
 * Request animation frame manager service, to handle all scroll and resize events in project.
 * Service based on requestAnimationFrame because it's much more efficient than scroll event.
 *
 * RequestAnimationFrameManager.addEventListener('scroll', () => { console.log('so great'); });
 * RequestAnimationFrameManager.removeEventListener('scroll', () => { console.log('so great'); });
 * RequestAnimationFrameManager.addEventListener('resize', () => { console.log('so great'); });
 * RequestAnimationFrameManager.removeEventListener('resize', () => { console.log('so great'); });
 * RequestAnimationFrameManager.setInterval(() => { console.log('so great'); }, 3000 });
 *
 */
const RequestAnimationFrameManager = (() => {
  const isBrowser = typeof window !== 'undefined';
  let animation = null;
  let events = [];
  let intervals = [];
  let scrollY = isBrowser ? window.scrollY : 0;
  let scrollX = isBrowser ? window.scrollX : 0;
  let windowWidth = isBrowser ? window.innerWidth : 0;
  let windowHeight = isBrowser ? window.innerHeight : 0;

  /**
   * Method for checking if scroll did happen
   */
  const didWindowScrollHappen = () => scrollY !== window.scrollY || scrollX !== window.scrollX;

  /**
   * Method for setting window scroll values
   * to compare them later with new one
   */
  const setWindowScrollPosition = () => {
    scrollY = window.scrollY;
    scrollX = window.scrollX;
  };

  /**
   * Method for checking if resize did happen
   */
  const didWindowResizeHappen = () => windowWidth !== window.innerWidth || windowHeight !== window.innerHeight;

  /**
   * Method for setting window resize values
   * to compare them later with new one
   */
  const setWindowResize = () => {
    windowWidth = window.innerWidth;
    windowHeight = window.innerHeight;
  };

  /**
   * Method calls a function or evaluates an expression at specified intervals (in milliseconds).
   * @param {function} fn - function that will be executed
   * @param {string} time -  intervals (in milliseconds) on how often to execute the code
   */
  const setInterval = (fn, time) => {
    intervals.push({
      fn,
      time,
      start: performance.now()
    });
  };

  /**
   * Method clears a timer set with the setInterval() method.
   * @param fn - function that will be removed
   */
  const clearInterval = fn => {
    intervals = intervals.filter(interval => interval.fn.name !== fn.name);
  };

  /**
   * Method to attach an event handler to a specified element.
   * @param {string} type - specifies the name of the event
   * @param {function} listener - specifies the function to run when the event occurs.
   */
  const addEventListener = (type, listener) => {
    events.push({
      type,
      listener
    });
  };

  /**
   * Method to remove an event handler that has been attached with the addEventListener() method.
   * @param {string} type - specifies the name of the event
   * @param {function} listener - specifies the function to run when the event occurs.
   */
  const removeEventListener = (type, listener) => {
    events = events.filter(event => event.type !== type || event.listener.name !== listener.name);
  };

  /**
   * Method that "kills" (cancels) requestAnimationFrame
   */
  const kill = () => {
    cancelAnimationFrame(animation);
  };

  /**
   * Method for doing magic with request animation frame
   * @param now
   */
  const animate = now => {
    if (intervals && intervals.length > 0) {
      Array.prototype.forEach.call(intervals, interval => {
        const {
          fn,
          time,
          start
        } = interval;
        const progress = Math.min((now - start) / time, 1);
        if (progress >= 1) {
          interval.start = performance.now();
          fn(now);
        }
      });
    }
    if (events && events.length > 0) {
      if (didWindowScrollHappen()) {
        setWindowScrollPosition();
        Array.prototype.forEach.call(events, fn => {
          if (fn.type === 'scroll') {
            fn.listener();
          }
        });
      }
      if (didWindowResizeHappen()) {
        setWindowResize();
        Array.prototype.forEach.call(events, fn => {
          if (fn.type === 'resize') {
            fn.listener();
          }
        });
      }
    }
    if (isBrowser) {
      animation = requestAnimationFrame(animate);
    }
  };

  /**
   * Run function
   */
  if (isBrowser) {
    animation = requestAnimationFrame(animate);
  }

  /**
   * Public APIs
   */
  return {
    addEventListener,
    removeEventListener,
    setInterval,
    clearInterval,
    kill
  };
})();
export default RequestAnimationFrameManager;